import styled from '@emotion/styled';
import { Box, Image, Spinner } from '@hover/blueprint';
import type { BoxProps } from '@hover/blueprint';
import type { MutableRefObject, TouchEventHandler } from 'react';
import React, { forwardRef, useEffect, useState } from 'react';

import type { ImageLike } from 'src/modules/renderings/imageLike';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

type CarouselImagesProps = {
  images: ImageLike[];
  onTouchEnd?: TouchEventHandler;
  onTouchMove?: TouchEventHandler;
  onTouchStart?: TouchEventHandler;
  selectedImageRef: MutableRefObject<HTMLImageElement | null>;
  selectedIndex: number;
};

const ImageBox = forwardRef<HTMLDivElement, BoxProps>((props, ref) => (
  <Box display="inline-block" height="100%" ref={ref} width="100%" {...props} />
));

const StyledImage = styled(Image)`
  touch-action: none;
  margin: 0 auto;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  display: block;
  object-fit: scale-down;
`;

const LazyImage = ({
  alt,
  src,
  isActive,
}: {
  alt: string;
  src: string;
  isActive: boolean;
}) => {
  const [fetched, setFetched] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!fetched && isActive) {
      setFetched(true);
    }
  }, [isActive, fetched]);

  const finishedLoading = () => setLoaded(true);

  return (
    <>
      {!loaded && (
        <Box
          alignItems="center"
          bottom={0}
          justifyContent="center"
          left={0}
          position="absolute"
          right={0}
          top={0}
        >
          <Spinner />
        </Box>
      )}
      <StyledImage
        alt={alt}
        borderTopRadius={{ base: undefined, desktop: '4px' }}
        // the loading prop not totally necessarily for this as it only
        // lazy loads images below the fold and not hidden images but may be useful
        // if our layout changes in the future
        loading="lazy"
        onLoad={finishedLoading}
        opacity={loaded ? 1 : 0}
        src={fetched ? src : ''}
        transitionDuration="200ms"
        transitionProperty="opacity"
      />
    </>
  );
};

const CarouselImages = ({
  images,
  onTouchEnd,
  onTouchMove,
  onTouchStart,
  selectedImageRef,
  selectedIndex,
}: CarouselImagesProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  return (
    <>
      {[images[images.length - 1], ...images, images[0]].map(
        (img, idx, infiniteImages) => {
          // Adjust idx by -1 to account for the added "infinite" images
          const isSelected = idx - 1 === selectedIndex;

          let imageBoxKey = img?.blobUrl;
          if (idx === infiniteImages.length - 1) {
            imageBoxKey = 'infinite-last';
          } else if (idx === 0) {
            imageBoxKey = 'infinite-first';
          }

          return (
            <ImageBox
              draggable={false}
              key={imageBoxKey}
              onTouchEnd={isSelected ? onTouchEnd : undefined}
              onTouchMove={isSelected ? onTouchMove : undefined}
              onTouchStart={isSelected ? onTouchStart : undefined}
              position="relative"
              ref={isSelected ? selectedImageRef : undefined}
              testId={isSelected ? 'carousel-selected-image' : undefined}
            >
              <LazyImage
                alt={img?.label(t)}
                isActive={isSelected}
                src={img?.blobUrl}
              />
            </ImageBox>
          );
        },
      )}
    </>
  );
};

export { Image, CarouselImages };
